import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Spinner,
  Banner
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { apiHelpers } from '../utils/api';

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  // Theme app embed (storefront enforcement) setup status. null = not yet loaded.
  const [embed, setEmbed] = useState(null);
  // The "enable purchase limits" setting (settings.enableGlobalLimits). null until loaded.
  const [limitsEnabled, setLimitsEnabled] = useState(null);

  useEffect(() => {
    loadStats();
    loadEmbedStatus();
    loadSettings();
  }, []);

  // Re-check the banner inputs whenever the merchant returns to this tab. The
  // "Enable Order Guard" link opens the theme editor in a new tab; when they
  // enable the embed there and come back, this picks up the change so the banner
  // flips to green without a manual refresh. Only the banner inputs are
  // refetched (no page-level spinner), so the stat cards don't flash.
  useEffect(() => {
    const refreshBannerState = () => {
      if (document.visibilityState === 'visible') {
        loadEmbedStatus();
        loadSettings();
      }
    };
    document.addEventListener('visibilitychange', refreshBannerState);
    window.addEventListener('focus', refreshBannerState);
    return () => {
      document.removeEventListener('visibilitychange', refreshBannerState);
      window.removeEventListener('focus', refreshBannerState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiHelpers.getDashboardStats();
      setStats(response.data);
    } catch (err) {
      console.error('Dashboard stats load error:', err);
      setError('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  // Load whether the Order Guard app embed is active on the live theme, so we can
  // prompt the merchant to switch it on. Tolerates failure — a detection error
  // shouldn't break the page; it falls back to the setup prompt.
  const loadEmbedStatus = async () => {
    try {
      const response = await apiHelpers.getEmbedStatus();
      setEmbed(response.data);
    } catch (err) {
      console.error('Embed status load error:', err);
      setEmbed({ enabled: null, everEnabled: null, deepLinkUrl: null, detectionError: true });
    }
  };

  // Load the "enable purchase limits" setting. The green "active" banner requires
  // it to be on, so we need its value to decide which banner to show. On failure
  // fall back to its schema default (enabled) rather than wrongly reporting off.
  const loadSettings = async () => {
    try {
      const response = await apiHelpers.getSettings();
      setLimitsEnabled(Boolean(response.data?.settings?.enableGlobalLimits));
    } catch (err) {
      console.error('Settings load error:', err);
      setLimitsEnabled(true);
    }
  };

  // Order Guard status banner. Enforcement is "active" only when BOTH the theme
  // app embed is on AND the "enable purchase limits" setting is ticked, so the
  // green banner is gated on both. Decision order:
  //   embed unverifiable (null)        -> yellow "couldn't check"
  //   embed off (false)                -> red "finish setup" (ignores the setting)
  //   embed on + setting still loading -> render nothing (avoid a green/red flash)
  //   embed on + setting off           -> red "Order Guard Inactive" -> Settings
  //   embed on + setting on            -> green "Order Guard Active"
  const renderEmbedBanner = () => {
    if (!embed) return null;

    if (embed.enabled === null) {
      return (
        <Layout.Section>
          <Banner
            status="warning"
            title="We couldn't check whether Order Guard is switched on"
            action={
              embed.deepLinkUrl
                ? { content: 'Open theme editor', url: embed.deepLinkUrl, external: true }
                : undefined
            }
          >
            <p>
              We couldn&rsquo;t read your live theme to confirm storefront enforcement is active. If
              you&rsquo;ve already enabled the Order Guard app embed, you can ignore this. Otherwise,
              open the theme editor to switch it on, then click <strong>Save</strong> in Shopify.
            </p>
          </Banner>
        </Layout.Section>
      );
    }

    if (embed.enabled === false) {
      return (
        <Layout.Section>
          <Banner
            status="critical"
            title="Finish setup: turn on Order Guard in your theme"
            action={
              embed.deepLinkUrl
                ? { content: 'Enable Order Guard', url: embed.deepLinkUrl, external: true }
                : undefined
            }
          >
            <p>
              Click the button below and then click the <strong>Save</strong> button.
            </p>
          </Banner>
        </Layout.Section>
      );
    }

    // Theme embed is active — gate the green banner on the purchase-limits setting.
    if (limitsEnabled === null) return null;

    if (limitsEnabled === false) {
      return (
        <Layout.Section>
          <Banner
            status="critical"
            title="Order Guard Inactive"
            action={{ content: 'Settings', onAction: () => navigate('/settings') }}
          >
            <p>To activate Order Guard, enable purchase limits on the Settings page.</p>
          </Banner>
        </Layout.Section>
      );
    }

    return (
      <Layout.Section>
        <Banner status="success" title="Order Guard Active" />
      </Layout.Section>
    );
  };

  const statsCards = stats
    ? [
        {
          title: 'Products with limits',
          value: stats.productsWithLimits,
          action: () => navigate('/products')
        },
        {
          title: 'Customer overrides',
          value: stats.activeOverrides,
          action: () => navigate('/customers')
        },
        {
          title: 'Orders blocked this month',
          value: stats.blockedThisMonth,
          action: null
        },
        {
          title: 'Orders blocked (all time)',
          value: stats.blockedTotal,
          action: null
        }
      ]
    : [];

  return (
    <Page
      title="Home"
    >
      <Layout>
        {/* Storefront enforcement (theme app embed) status / setup prompt */}
        {renderEmbedBanner()}

        {error && (
          <Layout.Section>
            <Banner status="critical" title="Error">
              <p>{error}</p>
              <Button onClick={loadStats}>Retry</Button>
            </Banner>
          </Layout.Section>
        )}

        {loading ? (
          <Layout.Section>
            <Card>
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        ) : (
          <Layout.Section>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px'
              }}
            >
              {statsCards.map((stat, index) => (
                <Card key={index}>
                  <div
                    className={`og-stat-card${stat.action ? ' og-stat-card--clickable' : ''}`}
                    onClick={stat.action || undefined}
                  >
                    <div className="og-stat-card__value">{stat.value}</div>
                    <div className="og-stat-card__label">{stat.title}</div>
                    {stat.action && (
                      <span className="og-stat-card__chevron" aria-hidden="true">
                        ›
                      </span>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}

export default Dashboard;
